/**
 * Auth context + useAuth hook.
 *
 * On mount, calls GET /auth/me. If the gateway returns 401 the user is not
 * logged in and we do a full browser navigation to /api/auth/login (the
 * gateway BFF starts the Keycloak auth-code + PKCE flow). We must NOT use a
 * fetch() redirect here because the auth flow sets httpOnly cookies that only
 * work over a real browser navigation.
 *
 * A global React Query error handler catches 401 on any subsequent request and
 * re-triggers the login redirect so an expired session is handled transparently.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

import type { Me } from '../api/types'
import { apiClient } from '../api/client'
import { redirectToLogin } from '../lib/session'

interface AuthState {
  loading: boolean
  me: Me | null
  /** True if the current user has the given permission */
  can: (perm: string) => boolean
}

const AuthContext = createContext<AuthState>({
  loading: true,
  me: null,
  can: () => false,
})

/** Backoff for a `/auth/me` that failed for a reason other than "not signed in". */
export const ME_RETRY_DELAYS_MS = [1000, 3000, 9000]

/**
 * Activity-gated keepalive. The gateway only refreshes the session (which is what
 * resets Keycloak's 30-minute idle clock) when a request arrives, and polling
 * pauses in a hidden tab — so someone working in the viewer tab for half an hour
 * came back to a /work tab whose session had died. One cheap `/auth/me` at most
 * every 4 minutes, and only after real input in this tab.
 */
export const KEEPALIVE_EVERY_MS = 4 * 60 * 1000

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [me, setMe] = useState<Me | null>(null)
  const [unreachable, setUnreachable] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const tryOnce = (n: number) => {
      apiClient
        .getMe()
        .then((data) => {
          if (cancelled) return
          setMe(data)
          setUnreachable(false)
          setLoading(false)
        })
        .catch((err) => {
          if (cancelled) return
          if (err?.status === 401) {
            // Not authenticated — full browser navigation to gateway BFF login
            redirectToLogin()
            return
          }
          // A 503 from a Keycloak blip or a deploy restarting the gateway. This used
          // to stop here with `me = null`, and every route renders nothing for a
          // null user — a blank page with no error and no way out but a reload.
          if (n < ME_RETRY_DELAYS_MS.length) {
            timer = setTimeout(() => tryOnce(n + 1), ME_RETRY_DELAYS_MS[n])
            return
          }
          setUnreachable(true)
          setLoading(false)
        })
    }
    tryOnce(0)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [attempt])

  useEffect(() => {
    if (!me) return
    let lastInput = Date.now()
    let lastPing = Date.now()
    const onInput = () => {
      lastInput = Date.now()
    }
    const types = ['pointerdown', 'keydown', 'wheel'] as const
    for (const t of types) window.addEventListener(t, onInput, { passive: true, capture: true })
    const id = setInterval(() => {
      const now = Date.now()
      if (now - lastInput > KEEPALIVE_EVERY_MS || now - lastPing < KEEPALIVE_EVERY_MS) return
      lastPing = now
      apiClient.getMe().catch((err) => {
        if (err?.status === 401) redirectToLogin()
      })
    }, 30_000)
    return () => {
      clearInterval(id)
      for (const t of types) window.removeEventListener(t, onInput, { capture: true })
    }
  }, [me])

  const can = (perm: string) => me?.permissions.includes(perm) ?? false

  if (unreachable && !me) {
    return (
      <div role="alert" className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-h3">Can’t reach the server right now</p>
        <p className="max-w-md text-sm text-muted-foreground">
          Nothing you saved is lost. This usually clears within a minute, for example while the
          platform is being updated.
        </p>
        <button
          type="button"
          className="rounded-full bg-primary px-4 py-2 text-sm text-primary-foreground"
          onClick={() => {
            setUnreachable(false)
            setLoading(true)
            setAttempt((a) => a + 1)
          }}
        >
          Try again
        </button>
      </div>
    )
  }

  return (
    <AuthContext.Provider value={{ loading, me, can }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  return useContext(AuthContext)
}
